/**
 * Perpendicular wall-thickness probe for linear opening footprints.
 *
 * A floorplan OPENING is drawn as a LINE on the wall, so its cutter needs a
 * thickness it cannot read off the geometry. The old fallback blasted a
 * symmetric 1m box "because overshoot is harmless" — false at corners, where
 * the overshoot crosses the junction and carves a chunk out of the adjacent
 * wall. This probe casts rays perpendicular to the footprint line against
 * the floor's room/outline rings and takes each side's FIRST boundary
 * crossing (median over three sample points, so one ray slipping through a
 * ring gap doesn't skew the result): the wall physically ends at its first
 * boundary, so the cutter never needs to reach past it.
 *
 * Pure 2D math — no viewer/scene imports — so it stays unit-testable.
 */

export interface ProbeXY {
  x: number;
  y: number;
}

/** Per-side reach along ±`wallNorm` of the probed line, in meters. */
export interface WallDepthProbeResult {
  /** Depth on the +wallNorm side. */
  positive: number;
  /** Depth on the −wallNorm side. */
  negative: number;
}

/** A side with no boundary crossing within reach reports this default —
 *  nothing is there to cut, so any modest overshoot is truly harmless. */
export const PROBE_MAX_SIDE_DEPTH_M = 0.5;
/** Never report a side thinner than this: a footprint drawn exactly ON a
 *  face still needs the cutter to clear that face. */
const PROBE_MIN_SIDE_DEPTH_M = 0.02;
/** Along-line sample fractions for the probe rays. */
const PROBE_SAMPLE_FRACTIONS = [0.25, 0.5, 0.75];

/** Distance along the ray (origin, unit dir) to segment p0→p1, or null when
 *  they don't cross ahead of the origin. */
function raySegmentDistance(origin: ProbeXY, dir: ProbeXY, p0: ProbeXY, p1: ProbeXY): number | null {
  const ex = p1.x - p0.x;
  const ey = p1.y - p0.y;
  const denom = dir.x * ey - dir.y * ex;
  if (Math.abs(denom) < 1e-12) return null;
  const dx = p0.x - origin.x;
  const dy = p0.y - origin.y;
  const t = (dx * ey - dy * ex) / denom;
  const u = (dx * dir.y - dy * dir.x) / denom;
  if (u < 0 || u > 1 || t < 0) return null;
  return t;
}

/**
 * Probe the wall depth on both sides of the line `center ± halfWidth·wallDir`
 * against `rings` (room polygons + outline rings, open or closed). Each side
 * reports the median first-crossing distance over the sample rays, clamped to
 * `[PROBE_MIN_SIDE_DEPTH_M, maxSideDepth]`; a side with no crossing reports
 * `maxSideDepth`.
 */
export function probeWallDepthsAtLine(center: ProbeXY, wallDir: ProbeXY, wallNorm: ProbeXY, halfWidth: number, rings: ProbeXY[][], maxSideDepth: number = PROBE_MAX_SIDE_DEPTH_M): WallDepthProbeResult {
  const sideDepth = (sign: 1 | -1): number => {
    const rayDir = { x: wallNorm.x * sign, y: wallNorm.y * sign };
    const hits: number[] = [];
    for (const f of PROBE_SAMPLE_FRACTIONS) {
      const u = (f * 2 - 1) * halfWidth;
      const origin = { x: center.x + wallDir.x * u, y: center.y + wallDir.y * u };
      let first: number | null = null;
      for (const ring of rings) {
        for (let i = 0; i < ring.length; i++) {
          const d = raySegmentDistance(origin, rayDir, ring[i], ring[(i + 1) % ring.length]);
          if (d !== null && d <= maxSideDepth && (first === null || d < first)) first = d;
        }
      }
      if (first !== null) hits.push(first);
    }
    if (hits.length === 0) return maxSideDepth;
    hits.sort((a, b) => a - b);
    return Math.max(PROBE_MIN_SIDE_DEPTH_M, hits[Math.floor(hits.length / 2)]);
  };
  return { positive: sideDepth(1), negative: sideDepth(-1) };
}
